import { Document } from '@contentful/rich-text-types';
import { ToastType } from '@everlywell/leaves';
import { IGatsbyImageData } from 'gatsby-plugin-image';

import { Charity } from './spreeProducts';

export type Variant = {
  id: number;
  is_master: boolean;
  price: string;
  price_in_dollars: string;
  sku: string;
  name?: string;
  subscription_plan?: SubscriptionPlan;
  total_on_hand?: number;
  membershipProductInfo?: ContentfulProductInfo;
};
export type SubscriptionPlan = {
  id: number;
  interval: string;
  interval_count: number;
  name: string;
  percent_discount: number;
};
export type Taxon = {
  id: number;
  name: string;
  taxonomy_id: number;
  taxonomy_name: string;
  position: number;
};
export type Product = {
  id: number;
  name: string;
  description: string;
  slug: string;
  sku: string;
  variants: Variant[];
  content_token: string;
  master_variant_id: number;
  lowest_subscription_price?: string | number;
  subscription: boolean;
  displayable: boolean;
  meta_title: string;
  meta_keywords: string;
  meta_description: string;
  taxons: Taxon[];
  position?: string;
  charities: Charity[];
  popular_description?: string;
  icon_image_url?: string;
};
export type MembershipProductType = Variant;
export type MembershipType = 'control' | 'sti';
export enum MembershipTypeOptions {
  control = 'control',
  current = 'sti',
}
export enum MembershipsRedirect {
  control = 'control-membership',
  current = 'current-membership',
}
export type SelectOption = {
  value: string | number;
  label: string;
};
export enum PageType {
  Homepage,
  PDP,
  PIP,
  None,
}
export type ImageType = {
  title?: string;
  description: string;
  gatsbyImageData: IGatsbyImageData;
  file?: {
    url: string;
  };
};

export type Image = {
  gatsbyImageData: IGatsbyImageData;
};

export interface IGatsbyImage {
  gatsbyImageData: IGatsbyImageData;
  description?: string;
}

export type ISchema = Record<string, string>;

export type FaqSchema = {
  question: string;
  answer: string;
}[];

export type PageContext = {
  nodeIndex: number;
  productInfoMap: ProductInfoMap;
  seoPage: boolean;
  subtitle: DynamicSubtitle;
  title: DynamicTitle;
  faqSchema?: FaqSchema;
  description: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  landingPageData?: any;
  productsBySlug?: ProductsBySlugType;
};

export type Marker = {
  title: string;
  description: {
    internal: {
      content: string;
    };
  };
};
export type Markers = {
  markers: Marker[];
};

export type ContentfulProductInfo = {
  boxImage?: IGatsbyImageData;
  displayName?: string;
  shortDescription?: string;
  medicalDisclaimer?: {
    medicalDisclaimer: string;
  };
  productCardImage?: ImageType;
  slug?: string;
  collectionMethods?: CollectionMethods[] | undefined;
  symptomList?: {
    symptoms: Symptom[];
  };
  category?: ContentfulProductCategory;
  id?: string | undefined;
};

export type RecommendedTest = {
  contentfulProductInfo: ContentfulProductInfo;
};

export type Symptom = {
  title: string;
};

export type CollectionMethods = {
  id: string;
  name: string;
};

export type ContentfulProductCategory = {
  id: string;
  title: string;
};

export type SuggestedProduct = {
  measures: string;
  title: string;
  displayName: string;
  slug: string;
  description: string;
  pdpImage: Image;
  price: number;
  productId: number;
  markerList: Markers;
  productCardImage?: Image;
};
export type ProductDataType = {
  measures: string;
  title: string;
  displayName: string;
  slug: string;
  description: string;
  pdpImage: Image;
  price: number;
  productId: number;
  markerList: Markers;
  productCardImage?: Image;
  sku: string;
  variantId: number;
  name: string;
  category: string;
  outOfStock?: boolean;
  checkAvailability?: boolean;
  popularityOrder?: number;
  onSale?: boolean;
  shortDescription?: string;
  promotionable?: boolean;
};
export type Content = {
  json: Document;
};
export type ContentSection = {
  title: string;
  headline: string;
  subHeadline: string;
  image: { file: { url: string } };
  description: { description: string };
  desktopImages: {
    gatsbyImageData: IGatsbyImageData;
  }[];
  listOfContentSections: ContentSection[];
};
export type AdditionalInfoType = {
  icon: Image;
  content?: Content;
  linkText?: string;
  linkUrl?: string;
  medicalReviewer?: string;
  medicalReviewerLink?: string;
  seoPage?: boolean;
};
export type DynamicTitle = {
  json: Document;
};
export type DynamicSubtitle = {
  json: Document;
};
export type ProductInfoMap = {
  [prop: number]: {
    price: string;
    variantId: number;
    sku: string;
    name?: string;
    slug: string;
  };
};
export type ProductInfoType = {
  price: string;
  variantId: number;
  sku: string;
  name?: string;
  slug: string;
  productId?: number;
};
export type SectionType = {
  backgroundColor: string;
  content: Content;
  funFact: AdditionalInfoType;
  image: Image;
  recommendedProducts: SuggestedProduct[];
  returnIcon: Image;
  returnText: string;
  subtitle: DynamicSubtitle;
  summaryText: string;
  title: string;
};
export type NavItem = {
  label: string;
  url?: string;
  link?: string;
  className?: string;
  dataTest?: string;
  dataAttributes?: Record<string, string>;
  style?: Record<string, string>;
  htmlId: string;
  classsName?: string;
  category?: string;
  items?: NavItem[];
};
export type SummaryType = {
  returnIcon: IGatsbyImageData;
  returnText: string;
  summaryText: string;
};
export type User = {
  id?: number | string;
  loggedIn?: boolean;
  isMember?: boolean;
};
// eslint-disable-next-line @typescript-eslint/ban-types
type PayloadType = string | number | boolean | object | Errors;
export type ReducerActionType = {
  type: string;
  payload: PayloadType;
};
export type LineItem = {
  quantity: number;
  variant_id: number; // eslint-disable-line camelcase
  id?: number;
  order_id?: number;
  product_id?: number;
  variant_sku?: string;
  product_name?: string;
  total?: string;
  amount?: string;
  base_price?: string;
  is_subscription?: boolean;
  subscription_frequency?: string | null;
  is_lab_visit?: boolean;
  max_quantity?: number;
};
export type AxiosErrorResponse = {
  response: {
    data: {
      errors: string[] | string;
      code: number;
      status: number;
      statusText: string;
      isAxiosError: boolean;
    };
  };
};
export type CTA = {
  text: string;
  url: string;
  appearance?: 'primary' | 'secondary' | 'hero';
};
export type ListOfContentWithImages = {
  identifier: string;
  featureFlag: boolean;
  description: {
    description: string;
  };
};
export type ContentWithImage = {
  slug?: string;
  heading: string;
  description?: {
    childMarkdownRemark: {
      html: string;
    };
    description: string;
  };
  image: IGatsbyImage;
  cta?: CTA;
  number?: number;
  identifier?: string;
  subheading?: string;
  featureFlag: boolean;
  listOfContentWithImages?: ListOfContentWithImages[];
};
export type PercentageWithLabel = {
  number: number;
  heading: string;
};
export type ContentWithImageAndCTA = {
  heading: string;
  description: {
    description: string;
  };
  image: IGatsbyImage;
  cta: CTA;
};
export type Slide = {
  heading: string;
  title?: string;
  description: {
    description: string;
  };
  image: IGatsbyImage;
  subheading: string;
};
export type PageSectionType = {
  sectionId: string;
  featureFlag: boolean;
  title?: string;
  headline?: string;
  subHeadline?: string;
  listOfContentWithImages?: ContentWithImage[];
  componentName: string;
  componentVariation?: string;
  mainCta?: CTA;
  backgroundColor?: string;
  slug?: string;
  subHeadlineDescription?: {
    childMarkdownRemark: {
      html: string;
    };
    subHeadlineDescription: string;
  };
  description?: {
    childMarkdownRemark?: {
      html: string;
    };
    description: string;
  };
  className?: string;
  embeddedVideoLink?: string;
  desktopImages?: ImageType[];
  mobileImages?: ImageType[];
};

export interface HeroSectionType {
  headline: string;
  mainCta?: CTA;
  description?: string;
  backgroundColor?: string;
  secondaryCtaList?: CTA[];
  secondaryHeadline?: string;
  desktopImage: {
    gatsbyImageData: IGatsbyImageData;
  };
  mobileImage: {
    gatsbyImageData: IGatsbyImageData;
  };
  listOfContentWithImages: Array<{
    heading: string;
    image: IGatsbyImage;
  }>;
  topBanner?: {
    headline: string;
    mainCta: {
      text: string;
      url: string;
    };
  };
}

export type ReactSpringHook = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controllers: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  start: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stop: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pause: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resume: any;
};
export type FormFieldType = {
  name: string;
  value: string;
};
type Promotion = {
  id: number;
  code: string;
  name: string;
  volume_discount_quantity?: number;
  description: string;
  line_item_discount: string;
};
export type Order = {
  id: number;
  email: string;
  number: string;
  user_id?: string;
  line_items: LineItem[];
  state: string;
  total: string;
  amount: string;
  ship_address_id?: string;
  bill_address_id?: string;
  promotions: Promotion[];
  promo_total: string;
  token: string;
  registration_token: string;
  shipping?: string;
};
export type Errors = string[] | null;
export type CartTotalType = {
  productQuantity: number;
  totalPrice: number;
  subTotal: number;
  currencyId: string;
  currencyFormat: string;
};
export type CartStateType = {
  cartTotal: CartTotalType;
  currentOrder: Order;
  lineItems: LineItem[];
  promoInputValue: string;
  currentPromoCode: string;
  shouldShowPromoInput: boolean;
  isPromoApplied: boolean;
  isOpen: boolean;
  showTOS: boolean;
  promoErrors: Errors;
  cartErrors: Errors;
};
export type ShippingAddressType = {
  addressLine: string[];
  city: string;
  country: string;
  dependentLocality: string;
  organization: string;
  phone: string;
  postalCode: string;
  recipient: string;
  region: string;
  sortingCode: string;
};
export type StripeCardType = {
  address_city: string;
  address_country: string;
  address_line1: string;
  address_line1_check: string;
  address_line2?: string;
  address_state: string;
  address_zip: string;
  address_zip_check: string;
  brand: string;
  country: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cvc_check?: any;
  dynamic_last4: string;
  exp_month: number;
  exp_year: number;
  funding: string;
  id: string;
  last4: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: any;
  name: string;
  object: string;
  tokenization_method: string;
};
export type FAQ = {
  question: string;
  answer: string;
};

export type CollectionMethodContentfulType = {
  v2Name: string;
  v2Icon: { file: { url: string } };
};
export type SubscriptionVariantType = Variant;
export interface ProductsBySlugType {
  [key: string]: {
    price: string;
    slug: string;
    canonicalUrl: string;
    name: string;
    boxImage: ImageType;
    cartImage: IGatsbyImageData;
    variantId: number;
    productId: number;
    reviewData: {
      average_rating: number;
      review_count: number;
      rating_histogram: number[];
      recommended_ratio: number;
    };
  };
}
export type ProductBySlugType = {
  contentfulProductInfo: ContentfulProductInfo;
  master_variant_id: number;
  productId: string;
  slug: string;
  variants: Variant[];
};

export type ProductBySKUType = {
  contentfulProductInfo: ContentfulProductInfo;
  master_variant_id: number;
  productId: string;
  sku: string;
  variants: Variant[];
};

export type FormattedProductForTracking = {
  name: string;
  id: number;
  price: number;
  variant: string;
  quantity: number;
  is_subscription: boolean | null;
  subscription_frequency: string | null;
};
export type NotificationType = {
  message: string;
  source: string;
  id: string;
  type?: ToastType;
  persist?: boolean;
  details?: JSX.Element | string;
  mixpanelLabel?: string;
  onlyShowOnce?: boolean;
};
export enum MembershipStatus {
  Active = 'active',
  Archived = 'archived',
  Cancelled = 'cancelled',
  Failed = 'failed',
  Paused = 'paused',
}
export const ActiveMembershipStatuses = [
  MembershipStatus.Active,
  MembershipStatus.Failed,
  MembershipStatus.Paused,
];
export type IterableProduct = {
  id: string | number;
  name: string;
  price: number;
};
export interface PDPPageSectionType extends PageSectionType {
  title: string;
  componentName: string;
  slug: string;
  listOfContentSections?: {
    sectionId: string;
    title: string;
    headline?: string;
    subHeadline?: string;
    image?: ImageType;
    description?: {
      description: string;
    };
    desktopImages?: Array<ImageType>;
    mobileImages?: Array<ImageType>;
    modalButtonText?: string;
    modalTitle?: string;
    slug?: string;
    embeddedVideoLink?: string;
    listOfContentSections?: Array<{
      title: string;
      image: ImageType;
      description: {
        description: string;
      };
    }>;
  };
  modalButtonText?: string;
  description?: {
    description: string;
  };
  desktopImages?: Array<ImageType>;
  mobileImages?: Array<ImageType>;
}
export interface AllProductInfoType {
  name: string;
  price: string;
  productId: number;
  pageSections: PDPPageSectionType[];
  heroSection: {
    consultAvailable: boolean;
    consultAvailableText: string;
    heroDescriptionHeader: string;
    heroDescriptionText: string;
    heroMeasuresText: string;
    heroMeasuresIcon?: {
      file: {
        url: string;
      };
    };
    heroPurchaseDetailsCallout: string;
    heroSliderImages: Array<ImageType>;
    heroTopCallout: string;
    heroBottomCallout: string;
    heroDescriptionCallout?: string;
    heroModalTitle: string;
    heroModalButtonText: string;
    heroMeasuresContentSection: {
      listOfContentSections: Array<{
        title: string;
        description: {
          description: string;
        };
        image: ImageType;
      }>;
    };
  };
  sku: string;
  slug: string;
  description: string;
  onSale: boolean;
  reviewData: {
    average_rating: number;
    review_count: number;
    rating_histogram: number[];
    recommended_ratio: number;
  };
  primaryCallouts: Array<{ text: string }>;
  markerList: {
    markers: Array<{
      title: string;
      description: {
        description: string;
      };
    }>;
  };
  metaDescription: string;
  metaKeywords: string;
  metaTitle: string;
  productSchema: ISchema;
  faqSchema: FaqSchema;
  variantId: number;
  collectionMethods: Array<CollectionMethodContentfulType>;
  subscriptionVariants: Array<SubscriptionVariantType>;
  digDeeperSections: Array<{ question: string; answer: { answer: string } }>;
  heroImages: Array<ImageType>;
  productsBySlug: ProductsBySlugType;
  hideSubscription?: boolean;
  preSelectedSubscription?: boolean;
  heroSubscriptionFaqUrl?: string;
  canonicalUrl: string;
  promotionable?: boolean;
  outOfStock: boolean | null;
  maxQuantity?: number;
}
export enum PaginationDirection {
  prev = 'prev',
  next = 'next',
}

export type ProductWithDiscountInfo = {
  name: string;
  price: string;
  slug: string;
  productId: number;
  variantId: number;
  promotionable?: boolean;
  memberPrice?: string;
  memberHeroCopy?: string;
};

export type CreditRedemptionRequest = { user_id: number; variant_id: number };

export type Announcement = {
  heading: string;
  subheading: string;
  body: {
    childMarkdownRemark: {
      html: string;
    };
  };
  primaryCta: {
    text: string;
    url?: string;
    type: 'primary' | 'secondary';
  };
  secondaryCta: {
    text: string;
    url?: string;
    type: 'primary' | 'secondary';
  };
  id: string;
  slug: string;
  sys: {
    revision: number;
  };
};

export type FormValueType = string | boolean | number;

export type FormValuesType = {
  [key: string]: FormValueType;
};

export type ContentfulReview = {
  product_name: string;
  product_page_id: string;
  average_rating: number;
  native_review_count: number;
  question_count: number;
  rating_histogram: number[];
  recommended_ratio: number;
  review_count: number;
  syndicated_review_count: number;
};

export type ProductPromoData = {
  slug: string;
  onSale: boolean;
  promoText: string;
};

export type ContentfulHtmlLongText = {
  childMarkdownRemark: {
    html: string;
  };
};
